import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useToast } from '../../../hooks';
import { ROUTES } from '../../../constants';

export default function PreferencesTab() {
  const { showToast } = useToast();
  const { user } = useSelector((state) => state.auth);
  
  const [preferences, setPreferences] = useState({
    theme: localStorage.getItem('theme') || 'light',
    defaultDashboard: user?.preferences?.defaultDashboard || '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Get available dashboards based on user role
  const getAvailableDashboards = () => {
    const allDashboards = [
      { path: ROUTES.EXECUTIVE_DASHBOARD, label: 'Executive Dashboard', icon: 'trending-up', roles: ['admin', 'executive'] },
      { path: '/department-management', label: 'Department Manager Hub', icon: 'users', roles: ['admin', 'manager'] },
      { path: ROUTES.STAFF_WORKFLOW, label: 'Staff Workflow Center', icon: 'clipboard', roles: ['admin', 'manager', 'staff'] },
      { path: ROUTES.INVENTORY, label: 'Inventory Management', icon: 'package', roles: ['admin', 'manager', 'staff'] },
      { path: ROUTES.FINANCIAL_REPORTS, label: 'Financial Reports', icon: 'file-text', roles: ['admin', 'executive', 'manager'] },
      { path: '/financial-analytics-dashboard', label: 'Financial Analytics', icon: 'bar-chart-2', roles: ['admin', 'executive'] },
      { path: ROUTES.BUDGET_MANAGEMENT, label: 'Budget Management', icon: 'dollar-sign', roles: ['admin', 'executive', 'manager'] },
      { path: ROUTES.USER_MANAGEMENT, label: 'User Management', icon: 'users-cog', roles: ['admin'] },
    ];

    return allDashboards.filter((dashboard) => 
      dashboard.roles.includes(user?.role)
    );
  };

  const availableDashboards = getAvailableDashboards();

  useEffect(() => {
    // Apply theme on mount and when it changes
    applyTheme(preferences.theme);
    
    // Listen for system theme changes when using system theme
    if (preferences.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = (e) => {
        applyTheme('system');
      };
      
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }
  }, [preferences.theme]);

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else if (theme === 'system') {
      // Check system preference
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (isDarkMode) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
    
    localStorage.setItem('theme', theme);
  };

  const handleThemeChange = (theme) => {
    setPreferences((prev) => ({ ...prev, theme }));
  };

  const handleDashboardChange = (e) => {
    setPreferences((prev) => ({ ...prev, defaultDashboard: e.target.value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: API call to save preferences
      // await updateUserPreferences(preferences);
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Apply theme immediately
      applyTheme(preferences.theme);
      
      showToast('Preferences saved successfully', 'success');
      
      // Update Redux store with new preferences
      // dispatch(updateUserPreferences(preferences));
    } catch (error) {
      showToast('Failed to save preferences', 'error');
      console.error('Error saving preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPreferences({
      theme: 'light',
      defaultDashboard: '',
    });
    applyTheme('light');
  };

  const hasChanges = 
    preferences.theme !== (localStorage.getItem('theme') || 'light') ||
    preferences.defaultDashboard !== (user?.preferences?.defaultDashboard || '');

  return (
    <div className="max-w-2xl space-y-8">
      {/* Theme Section */}
      <div>
        <h3 className="text-lg font-medium text-foreground mb-2">Appearance</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Customize how the ERP system looks on your device.
        </p>

        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground mb-3">
            Theme
          </label>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Light Theme */}
            <button
              onClick={() => handleThemeChange('light')}
              className={`
                relative border-2 rounded-lg p-4 transition-all duration-200
                ${preferences.theme === 'light'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-muted-foreground bg-card'
                }
              `}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-full h-24 bg-white border border-gray-200 rounded shadow-sm flex items-center justify-center">
                  <AppIcon name="Sun" className="w-8 h-8 text-yellow-500" />
                </div>
                <span className="text-sm font-medium text-foreground">Light</span>
                {preferences.theme === 'light' && (
                  <div className="absolute top-2 right-2">
                    <AppIcon name="CheckCircle" className="w-5 h-5 text-primary" />
                  </div>
                )}
              </div>
            </button>

            {/* Dark Theme */}
            <button
              onClick={() => handleThemeChange('dark')}
              className={`
                relative border-2 rounded-lg p-4 transition-all duration-200
                ${preferences.theme === 'dark'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-muted-foreground bg-card'
                }
              `}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-full h-24 bg-gray-900 border border-gray-700 rounded shadow-sm flex items-center justify-center">
                  <AppIcon name="Moon" className="w-8 h-8 text-blue-400" />
                </div>
                <span className="text-sm font-medium text-foreground">Dark</span>
                {preferences.theme === 'dark' && (
                  <div className="absolute top-2 right-2">
                    <AppIcon name="CheckCircle" className="w-5 h-5 text-primary" />
                  </div>
                )}
              </div>
            </button>

            {/* System Theme */}
            <button
              onClick={() => handleThemeChange('system')}
              className={`
                relative border-2 rounded-lg p-4 transition-all duration-200
                ${preferences.theme === 'system'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-muted-foreground bg-card'
                }
              `}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-full h-24 bg-gradient-to-r from-white to-gray-900 border border-gray-200 rounded shadow-sm flex items-center justify-center">
                  <AppIcon name="Monitor" className="w-8 h-8 text-gray-600" />
                </div>
                <span className="text-sm font-medium text-foreground">System</span>
                {preferences.theme === 'system' && (
                  <div className="absolute top-2 right-2">
                    <AppIcon name="CheckCircle" className="w-5 h-5 text-primary" />
                  </div>
                )}
              </div>
            </button>
          </div>

          <p className="text-xs text-muted-foreground mt-2">
            {preferences.theme === 'system'
              ? 'Theme will automatically switch based on your device settings'
              : `Using ${preferences.theme} theme`
            }
          </p>
        </div>
      </div>

      <div className="border-t border-border pt-8">
        <h3 className="text-lg font-medium text-foreground mb-2">Default Dashboard</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Choose which dashboard to show when you log in.
        </p>

        <div className="space-y-4">
          <label htmlFor="defaultDashboard" className="block text-sm font-medium text-foreground">
            Landing Page
          </label>
          
          <select
            id="defaultDashboard"
            value={preferences.defaultDashboard}
            onChange={handleDashboardChange}
            className="w-full px-3 py-2 bg-input border border-border rounded-md shadow-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Auto (Based on your role)</option>
            {availableDashboards.map((dashboard) => (
              <option key={dashboard.path} value={dashboard.path}>
                {dashboard.label}
              </option>
            ))}
          </select>

          {preferences.defaultDashboard && (
            <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <AppIcon name="Info" className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  You'll be redirected to{' '}
                  <strong>
                    {availableDashboards.find((d) => d.path === preferences.defaultDashboard)?.label}
                  </strong>{' '}
                  after logging in.
                </p>
              </div>
            </div>
          )}

          {!preferences.defaultDashboard && (
            <div className="flex items-start gap-3 p-4 bg-muted border border-border rounded-lg">
              <AppIcon name="Info" className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  Default behavior: You'll be redirected based on your role.
                </p>
                <ul className="mt-2 text-xs text-muted-foreground space-y-1">
                  <li>• Admin → Executive Dashboard</li>
                  <li>• Executive → Executive Dashboard</li>
                  <li>• Manager → Department Manager Hub</li>
                  <li>• Staff → Staff Workflow Center</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Display Settings */}
      <div className="border-t border-border pt-8">
        <h3 className="text-lg font-medium text-foreground mb-2">Display Settings</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Customize how data is displayed throughout the system.
        </p>

        <div className="space-y-4">
          {/* Items per Page */}
          <div>
            <label htmlFor="itemsPerPage" className="block text-sm font-medium text-foreground mb-2">
              Items per Page (Tables & Lists)
            </label>
            <select
              id="itemsPerPage"
              className="w-full px-3 py-2 bg-input border border-border rounded-md shadow-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              defaultValue="20"
            >
              <option value="10">10 items</option>
              <option value="20">20 items (Default)</option>
              <option value="50">50 items</option>
              <option value="100">100 items</option>
            </select>
          </div>

          {/* Table Density */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Table Density
            </label>
            <div className="grid grid-cols-3 gap-3">
              {['Compact', 'Comfortable', 'Spacious'].map((density) => (
                <button
                  key={density}
                  className="px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                >
                  {density}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button
          variant="ghost"
          onClick={handleReset}
          className="text-muted-foreground"
        >
          Reset to Defaults
        </Button>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <>
                <AppIcon name="Loader2" className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <AppIcon name="Check" className="w-4 h-4" />
                Save Preferences
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
